import { App } from 'vue'

import { Company } from '@ankor-io/iam/types'

import { AuthenticationContext, PrincipalIdentity } from '@/iam/types'
import { MixPanel, MixPanelPlugin } from '@/utils/mixPanel/types'

import { ServerMixpanelClient } from './serverClient'

/**
 * Server-side MixPanel instance
 */
export const mixPanelInstance = (function () {
  let _mixPanel: MixPanel | undefined

  const createMixPanelClient = async (
    authenticationContextOrToken: AuthenticationContext | string,
    tokenOrUser?: string | PrincipalIdentity,
    userOrCompany?: PrincipalIdentity | Company | null,
    company?: Company | null,
  ): Promise<MixPanelPlugin> => {
    // Handle both old and new parameter formats
    let authenticationContext: AuthenticationContext | null = null
    let token: string
    let user: PrincipalIdentity | null = null
    let companyValue: Company | null = null

    if (typeof authenticationContextOrToken === 'string') {
      // Old format: (token)
      token = authenticationContextOrToken
    } else {
      // New format: (authenticationContext, token, user, company)
      authenticationContext = authenticationContextOrToken
      token = tokenOrUser as string
      user = userOrCompany as PrincipalIdentity
      companyValue = company || null
    }
    // Create server-side client
    const serverClient = ServerMixpanelClient.createClient(authenticationContext, token, user, companyValue)

    // Create a wrapper that matches the MixPanel interface
    _mixPanel = {
      track: async (event_name: string, properties?: any, _optionsOrCallback?: any, callback?: any) => {
        await serverClient.track(event_name, properties)
        if (callback && typeof callback === 'function') {
          callback()
        }
      },
      identify: async (distinctId: string, properties?: Record<string, any>) => {
        await serverClient.identify(distinctId, properties)
      },
      people: {
        set: async (properties: Record<string, any>) => {
          await serverClient.peopleSet(properties)
        },
      },
      // Add other methods as needed for compatibility
      init: () => {},
      register: () => {},
      unregister: () => {},
      register_once: () => {},
      unregister_once: () => {},
      has_opted_in_tracking: () => false,
      has_opted_out_tracking: () => false,
      opt_in_tracking: () => {},
      opt_out_tracking: () => {},
      clear_opt_in_out_tracking: () => {},
      time_event: () => {},
      track_links: () => {},
      track_forms: () => {},
      track_pageview: () => {},
      alias: () => {},
      set_config: () => {},
      get_config: () => ({}),
      get_distinct_id: () => user?.id || 'anonymous',
      get_property: () => null,
      get_group: () => null,
      set_group: () => {},
      add_group: () => {},
      remove_group: () => {},
      delete_group: () => {},
      union: () => {},
      append: () => {},
      remove: () => {},
      set_once: () => {},
      increment: () => {},
      track_charge: () => {},
      clear_charges: () => {},
      delete_user: () => {},
      group: () => ({
        set: () => {},
        set_once: () => {},
        union: () => {},
        remove: () => {},
        delete_group_property: () => {},
        unset: () => {},
      }),
    } as MixPanel

    return {
      install: (app: App): void => {
        app.config.globalProperties.$mixPanel = _mixPanel!
        app.provide('mixPanel', _mixPanel!)
      },
    }
  }

  return {
    createMixPanelClient,
    getMixPanel: () => _mixPanel,
  }
})()
